/**
 * Server Authentication — PIN-based access control
 *
 * Server owner sets a PIN. Clients must enter it to connect.
 * Roles: admin (full access), entry (create/edit vouchers), viewer (read-only)
 */

export type UserRole = 'admin' | 'entry' | 'viewer';

interface ServerAuthConfig {
  pinEnabled: boolean;
  pin: string;
  maxClients: number;
  sessions: Map<string, { role: UserRole; connectedAt: number; lastActivity: number; clientIP: string }>;
}

const authConfig: ServerAuthConfig = {
  pinEnabled: false,
  pin: '',
  maxClients: 3,
  sessions: new Map(),
};

export function setServerPIN(pin: string): void {
  authConfig.pin = pin;
  authConfig.pinEnabled = pin.length > 0;
}

export function clearServerPIN(): void {
  authConfig.pin = '';
  authConfig.pinEnabled = false;
}

export function isPINEnabled(): boolean {
  return authConfig.pinEnabled;
}

export function setMaxClients(max: number): void {
  authConfig.maxClients = max;
}

export function getMaxClients(): number {
  return authConfig.maxClients;
}

export function validatePIN(pin: string): boolean {
  if (!authConfig.pinEnabled) return true;
  return pin === authConfig.pin;
}

export function createSession(clientIP: string, role: UserRole = 'entry'): string {
  if (authConfig.sessions.size >= authConfig.maxClients) {
    throw new Error(`Maximum ${authConfig.maxClients} clients allowed. Upgrade your plan for more.`);
  }

  const token = generateToken();
  authConfig.sessions.set(token, {
    role,
    connectedAt: Date.now(),
    lastActivity: Date.now(),
    clientIP,
  });
  return token;
}

export function validateSession(token: string): { valid: boolean; role: UserRole } {
  const session = authConfig.sessions.get(token);
  if (!session) return { valid: false, role: 'viewer' };
  session.lastActivity = Date.now();
  return { valid: true, role: session.role };
}

export function removeSession(token: string): void {
  authConfig.sessions.delete(token);
}

export function getActiveSessions(): { token: string; role: UserRole; clientIP: string; connectedAt: number }[] {
  return Array.from(authConfig.sessions.entries()).map(([token, s]) => ({
    token: token.substring(0, 8) + '...',
    role: s.role,
    clientIP: s.clientIP,
    connectedAt: s.connectedAt,
  }));
}

export function getActiveSessionCount(): number {
  return authConfig.sessions.size;
}

export function disconnectAllClients(): void {
  authConfig.sessions.clear();
}

export function canPerformAction(role: UserRole, action: 'read' | 'create' | 'update' | 'delete'): boolean {
  switch (role) {
    case 'admin':
      return true;
    case 'entry':
      return action !== 'delete';
    case 'viewer':
      return action === 'read';
    default:
      return false;
  }
}

function generateToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  for (let i = 0; i < 32; i++) {
    token += chars[array[i] % chars.length];
  }
  return token;
}
